import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BarChart3, Users, MapPin, Calendar, TrendingUp, TrendingDown, Download, IndianRupee, PiggyBank, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const AdminReports = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("current_month");
  // Indian Financial Year: April → March. fy_start_year (e.g. 2025 = FY 2025-26).
  const now = new Date();
  const currentFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState(currentFY.toString());
  const [agentReport, setAgentReport] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [monthReport, setMonthReport] = useState(null);
  const [yearReport, setYearReport] = useState(null);
  const [destinationReport, setDestinationReport] = useState(null);
  const [currentMonthSummary, setCurrentMonthSummary] = useState(null);
  const [agentConversion, setAgentConversion] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [profitabilityScope, setProfitabilityScope] = useState("current_month");
  const [loading, setLoading] = useState(false);

  // FY label like "FY 2025-26"
  const fyLabel = (y) => `FY ${y}-${String(Number(y) + 1).slice(2)}`;
  const years = Array.from({ length: 5 }, (_, i) => (currentFY - i).toString());

  useEffect(() => {
    fetchReports();
  }, [activeTab, selectedYear, profitabilityScope]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      if (activeTab === "current_month") {
        const res = await axios.get(`${API}/reports/current-month`, { headers });
        setCurrentMonthSummary(res.data);
      } else if (activeTab === "agent_conversion") {
        const res = await axios.get(`${API}/reports/agent-conversion?scope=current_month`, { headers });
        setAgentConversion(res.data);
      } else if (activeTab === "profitability") {
        const res = await axios.get(`${API}/reports/profitability?scope=${profitabilityScope}`, { headers });
        setProfitability(res.data);
      } else if (activeTab === "employee") {
        const res = await axios.get(`${API}/reports/employee-wise?year=${selectedYear}`, { headers });
        setEmployeeReport(res.data);
      } else if (activeTab === "agent") {
        const res = await axios.get(`${API}/reports/agent-wise?year=${selectedYear}`, { headers });
        setAgentReport(res.data);
      } else if (activeTab === "month") {
        const res = await axios.get(`${API}/reports/month-wise?year=${selectedYear}`, { headers });
        setMonthReport(res.data);
      } else if (activeTab === "year") {
        const res = await axios.get(`${API}/reports/year-wise`, { headers });
        setYearReport(res.data);
      } else if (activeTab === "destination") {
        const res = await axios.get(`${API}/reports/destination-wise?year=${selectedYear}`, { headers });
        setDestinationReport(res.data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map(row => headers.map(h => row[h]).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${selectedYear}.csv`;
    a.click();
  };

  const GrowthIndicator = ({ value }) => {
    if (!value || value === 0) return <span className="text-gray-500">-</span>;
    const isPositive = value > 0;
    return (
      <span className={`flex items-center gap-1 ${isPositive ? "text-green-400" : "text-red-400"}`}>
        {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {Math.abs(value)}%
      </span>
    );
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-reports">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Reports & Analytics</h1>
            <p className="text-gray-500">Comprehensive business reports and insights</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-gray-50">
              <TabsTrigger value="current_month" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" data-testid="tab-current-month">
                <Calendar className="w-4 h-4 mr-2" /> Current Month
              </TabsTrigger>
              <TabsTrigger value="agent_conversion" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" data-testid="tab-agent-conversion">
                <TrendingUp className="w-4 h-4 mr-2" /> Agent Conversion
              </TabsTrigger>
              <TabsTrigger value="profitability" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white" data-testid="tab-profitability">
                <PiggyBank className="w-4 h-4 mr-2" /> Profitability
              </TabsTrigger>
              <TabsTrigger value="employee" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Employee-wise
              </TabsTrigger>
              <TabsTrigger value="agent" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Users className="w-4 h-4 mr-2" /> Agent-wise
              </TabsTrigger>
              <TabsTrigger value="month" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <Calendar className="w-4 h-4 mr-2" /> Month-wise
              </TabsTrigger>
              <TabsTrigger value="year" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4 mr-2" /> Year-wise
              </TabsTrigger>
              <TabsTrigger value="destination" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                <MapPin className="w-4 h-4 mr-2" /> Destination-wise
              </TabsTrigger>
            </TabsList>

            {activeTab !== "year" && !["current_month", "agent_conversion", "profitability"].includes(activeTab) && (
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-44 bg-gray-50 border-gray-200 text-gray-800" data-testid="reports-fy-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year}>{fyLabel(year)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Current Month Sales Report */}
          <TabsContent value="current_month" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="current-month-card">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-gray-800 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                    Current Month Sales Report
                    {currentMonthSummary?.month_label && (
                      <span className="text-sm font-normal text-gray-500">— {currentMonthSummary.month_label}</span>
                    )}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="email-monthly-pnl-btn"
                    onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/admin/reports/monthly-profitability/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
                        const sent = res.data?.sent ?? 0;
                        const lbl = res.data?.summary?.month_label || "last month";
                        if (sent > 0) {
                          toast.success(`Monthly P&L email sent to ${sent} admin${sent > 1 ? 's' : ''} (${lbl})`);
                        } else {
                          toast.info(`Monthly P&L email queued for ${lbl}. Check SMTP config if no admin received it.`);
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to send monthly P&L email");
                      }
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" /> Email Last Month P&L
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading || !currentMonthSummary ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-testid="cm-inquiries">
                      <p className="text-sm text-blue-600">Inquiries</p>
                      <p className="text-2xl font-bold text-blue-800">{currentMonthSummary.inquiries}</p>
                    </div>
                    <div className="bg-pink-50 rounded-lg p-4 border border-pink-100" data-testid="cm-quotes">
                      <p className="text-sm text-pink-600">Quotes</p>
                      <p className="text-2xl font-bold text-pink-800">{currentMonthSummary.quotes}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 border border-green-100" data-testid="cm-confirmed">
                      <p className="text-sm text-green-600">Confirmed Bookings</p>
                      <p className="text-2xl font-bold text-green-800">{currentMonthSummary.confirmed_bookings}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-100" data-testid="cm-booking-amt">
                      <p className="text-sm text-amber-600">Booking Amount</p>
                      <p className="text-2xl font-bold text-amber-800">{formatCurrency(currentMonthSummary.total_booking_amount)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-testid="cm-received">
                      <p className="text-sm text-emerald-600">Received Amount</p>
                      <p className="text-2xl font-bold text-emerald-800">{formatCurrency(currentMonthSummary.total_received)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100" data-testid="cm-balance">
                      <p className="text-sm text-red-600">Balance Amount</p>
                      <p className="text-2xl font-bold text-red-800">{formatCurrency(currentMonthSummary.balance_amount)}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-100" data-testid="cm-vendor">
                      <p className="text-sm text-orange-600">Vendor Paid</p>
                      <p className="text-2xl font-bold text-orange-800">{formatCurrency(currentMonthSummary.total_vendor_paid)}</p>
                    </div>
                    <div className="bg-violet-50 rounded-lg p-4 border border-violet-100" data-testid="cm-gst">
                      <p className="text-sm text-violet-600">GST ({Math.round((currentMonthSummary.gst_rate || 0) * 100)}%)</p>
                      <p className="text-2xl font-bold text-violet-800">{formatCurrency(currentMonthSummary.total_gst)}</p>
                    </div>
                    <div className="md:col-span-4 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg p-5 text-white shadow-lg" data-testid="cm-net-profit">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-sm uppercase tracking-wide font-semibold">Net Profit (After Vendor Payments + GST)</p>
                          <p className="text-3xl font-bold mt-1">{formatCurrency(currentMonthSummary.net_profit)}</p>
                          <p className="text-emerald-100 text-xs mt-1">Booking Amt − Vendor Paid − GST</p>
                        </div>
                        <PiggyBank className="w-12 h-12 text-emerald-200" />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent Conversion Report */}
          <TabsContent value="agent_conversion" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-conversion-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Agent-wise Booking Report — Current Month
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportToCSV(agentConversion?.data, "agent_conversion_current_month")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !agentConversion?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No agent activity this month yet.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <p className="text-sm text-blue-600">Total Quotations</p>
                        <p className="text-2xl font-bold text-blue-800">{agentConversion.totals.quotations}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                        <p className="text-sm text-green-600">Confirmed Bookings</p>
                        <p className="text-2xl font-bold text-green-800">{agentConversion.totals.confirmations}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                        <p className="text-sm text-purple-600">Overall Conversion</p>
                        <p className="text-2xl font-bold text-purple-800">{agentConversion.totals.conversion_rate}%</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="agent-conversion-table">
                        <thead className="bg-gray-50 border-y border-gray-200">
                          <tr>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Agent Company</th>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Agent Name</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">No. of Quotations</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">No. of Confirmations</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Conversion Rate</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Booking Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {agentConversion.data.map((a, i) => (
                            <tr key={a.agent_id || i} className="hover:bg-gray-50" data-testid={`agent-conv-row-${i}`}>
                              <td className="py-3 px-4 text-gray-800 font-medium">{a.company_name || "—"}</td>
                              <td className="py-3 px-4 text-gray-600">{a.agent_name}</td>
                              <td className="text-center py-3 px-3 text-gray-700">{a.quotations}</td>
                              <td className="text-center py-3 px-3 text-emerald-700 font-semibold">{a.confirmations}</td>
                              <td className="text-center py-3 px-3">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  a.conversion_rate >= 50 ? "bg-emerald-100 text-emerald-700" :
                                  a.conversion_rate >= 20 ? "bg-amber-100 text-amber-700" :
                                  "bg-red-100 text-red-700"
                                }`}>{a.conversion_rate}%</span>
                              </td>
                              <td className="text-right py-3 px-3 text-gray-800 font-semibold">{formatCurrency(a.booking_amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profitability Report */}
          <TabsContent value="profitability" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="profitability-card">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-gray-800 flex items-center gap-2">
                  <PiggyBank className="w-5 h-5 text-emerald-600" />
                  Per-Booking Profitability — {profitability?.scope_label || "Current Month"}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={profitabilityScope} onValueChange={setProfitabilityScope}>
                    <SelectTrigger className="w-48 bg-gray-50 border-gray-200 text-gray-800" data-testid="profitability-scope-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_month">Current Month</SelectItem>
                      <SelectItem value="this_fy">This Financial Year</SelectItem>
                      <SelectItem value="last_fy">Last Financial Year</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToCSV(profitability?.data, `profitability_${profitabilityScope}`)}
                    className="border-gray-200 text-gray-600"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !profitability?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No bookings in this period yet.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                        <p className="text-xs text-amber-600">Booking Amount</p>
                        <p className="text-xl font-bold text-amber-800">{formatCurrency(profitability.totals.booking_amount)}</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                        <p className="text-xs text-orange-600">Vendor Paid</p>
                        <p className="text-xl font-bold text-orange-800">{formatCurrency(profitability.totals.vendor_paid)}</p>
                      </div>
                      <div className="bg-violet-50 rounded-lg p-4 border border-violet-100">
                        <p className="text-xs text-violet-600">GST</p>
                        <p className="text-xl font-bold text-violet-800">{formatCurrency(profitability.totals.gst_amount)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                        <p className="text-xs text-emerald-600">Net Profit</p>
                        <p className="text-xl font-bold text-emerald-800">{formatCurrency(profitability.totals.net_profit)}</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <p className="text-xs text-blue-600">Margin</p>
                        <p className="text-xl font-bold text-blue-800">{profitability.totals.profit_margin}%</p>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="profitability-table">
                        <thead className="bg-gray-50 border-y border-gray-200">
                          <tr>
                            <th className="text-left py-3 px-3 font-semibold text-gray-700">Booking</th>
                            <th className="text-left py-3 px-3 font-semibold text-gray-700">Agent</th>
                            <th className="text-left py-3 px-3 font-semibold text-gray-700">Guest / Destination</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Booking Amt</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Vendor Paid</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">GST</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Net Profit</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Margin</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {profitability.data.map((b, i) => (
                            <tr key={b.booking_id || i} className="hover:bg-gray-50" data-testid={`profit-row-${i}`}>
                              <td className="py-3 px-3">
                                <span className="font-mono text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-xs">#{b.booking_ref}</span>
                              </td>
                              <td className="py-3 px-3 text-gray-700">{b.agent_name}</td>
                              <td className="py-3 px-3">
                                <div className="text-gray-800">{b.guest_name}</div>
                                <div className="text-xs text-gray-500">{b.destination}</div>
                              </td>
                              <td className="text-right py-3 px-3 text-gray-800 font-semibold">{formatCurrency(b.booking_amount)}</td>
                              <td className="text-right py-3 px-3 text-orange-700">{formatCurrency(b.vendor_paid)}</td>
                              <td className="text-right py-3 px-3 text-violet-700">{formatCurrency(b.gst_amount)}</td>
                              <td className={`text-right py-3 px-3 font-bold ${b.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                                {formatCurrency(b.net_profit)}
                              </td>
                              <td className="text-center py-3 px-3">
                                <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                  b.profit_margin >= 30 ? "bg-emerald-100 text-emerald-700" :
                                  b.profit_margin >= 10 ? "bg-amber-100 text-amber-700" :
                                  "bg-red-100 text-red-700"
                                }`}>{b.profit_margin}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Employee-wise Sales Report */}
          <TabsContent value="employee" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Employee Sales Report - {fyLabel(selectedYear)}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(employeeReport?.data, "employee_sales_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !employeeReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data available for {selectedYear}</p>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                        <p className="text-sm text-blue-600">Total Quotes</p>
                        <p className="text-2xl font-bold text-blue-800">{employeeReport.totals?.total_quotes || 0}</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                        <p className="text-sm text-green-600">Booked</p>
                        <p className="text-2xl font-bold text-green-800">{employeeReport.totals?.booked_quotes || 0}</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                        <p className="text-sm text-purple-600">Quote Value</p>
                        <p className="text-2xl font-bold text-purple-800">{formatCurrency(employeeReport.totals?.quote_value || 0)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                        <p className="text-sm text-emerald-600">Revenue</p>
                        <p className="text-2xl font-bold text-emerald-800">{formatCurrency(employeeReport.totals?.revenue || 0)}</p>
                      </div>
                    </div>

                    {/* Bar Chart */}
                    <div className="h-64 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={employeeReport.data.filter(e => e.total_quotes > 0)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="employee_name" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0" }}
                            formatter={(value, name) => [name === 'revenue' ? formatCurrency(value) : value, name]}
                          />
                          <Legend />
                          <Bar dataKey="total_quotes" name="Total Quotes" fill="#3b82f6" />
                          <Bar dataKey="booked_quotes" name="Booked" fill="#10b981" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Detailed Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-y border-gray-200">
                          <tr>
                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Employee</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Total Quotes</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">New</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">In Progress</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Approved</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Booked</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Lost</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Quote Value</th>
                            <th className="text-right py-3 px-3 font-semibold text-gray-700">Revenue</th>
                            <th className="text-center py-3 px-3 font-semibold text-gray-700">Conv. %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {employeeReport.data.map((emp, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-gray-900">{emp.employee_name}</p>
                                  <p className="text-xs text-gray-500">{emp.employee_email}</p>
                                </div>
                              </td>
                              <td className="text-center py-3 px-3 font-semibold text-gray-800">{emp.total_quotes}</td>
                              <td className="text-center py-3 px-3 text-gray-600">{emp.new_quotes}</td>
                              <td className="text-center py-3 px-3 text-blue-600">{emp.in_progress}</td>
                              <td className="text-center py-3 px-3 text-green-600">{emp.approved_quotes}</td>
                              <td className="text-center py-3 px-3">
                                <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">{emp.booked_quotes}</span>
                              </td>
                              <td className="text-center py-3 px-3 text-red-500">{emp.lost_quotes}</td>
                              <td className="text-right py-3 px-3 text-gray-700">{formatCurrency(emp.quote_value)}</td>
                              <td className="text-right py-3 px-3 font-semibold text-emerald-600">{formatCurrency(emp.revenue)}</td>
                              <td className="text-center py-3 px-3">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  emp.conversion_rate >= 30 ? 'bg-green-100 text-green-700' :
                                  emp.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {emp.conversion_rate}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agent-wise Report */}
          <TabsContent value="agent" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Agent Performance Report - {fyLabel(selectedYear)}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(agentReport?.data, "agent_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !agentReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data available for {selectedYear}</p>
                ) : (
                  <>
                    <div className="h-64 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={agentReport.data.slice(0, 10)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="agent_name" stroke="#94a3b8" fontSize={12} />
                          <YAxis stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                          />
                          <Legend />
                          <Bar dataKey="total_quotes" name="Quotes" fill="#2E7D32" />
                          <Bar dataKey="total_bookings" name="Bookings" fill="#C9A227" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Agent</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Approved</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Booked</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Adults</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Children</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Infants</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quote Value</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {agentReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800">{row.agent_name}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_quotes}</td>
                              <td className="px-4 py-3 text-right text-green-400">{row.approved_quotes}</td>
                              <td className="px-4 py-3 text-right text-blue-400">{row.booked_quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_adults}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_children}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_infants}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(row.quote_value)}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_bookings}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.total_revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Month-wise Report */}
          <TabsContent value="month" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Monthly Report - {fyLabel(selectedYear)}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(monthReport?.data, "month_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !monthReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data available</p>
                ) : (
                  <>
                    <div className="h-72 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthReport.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="month_name" stroke="#94a3b8" fontSize={12} />
                          <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} />
                          <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                            formatter={(value, name) => [name === "revenue" ? formatCurrency(value) : value, name]}
                          />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="inquiries" name="Inquiries" stroke="#60a5fa" strokeWidth={2} />
                          <Line yAxisId="left" type="monotone" dataKey="quotes" name="Quotes" stroke="#2E7D32" strokeWidth={2} />
                          <Line yAxisId="left" type="monotone" dataKey="bookings" name="Bookings" stroke="#C9A227" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Month</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Inquiries</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quote Value</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {monthReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800">{row.month_label || `${row.month_name} ${row.year || selectedYear}`}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.inquiries}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(row.quote_value)}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.bookings}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50/30">
                          <tr>
                            <td className="px-4 py-3 text-gray-800 font-semibold">Total</td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.inquiries, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.quotes, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {formatCurrency(monthReport.data.reduce((s, r) => s + r.quote_value, 0))}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                              {monthReport.data.reduce((s, r) => s + r.bookings, 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-emerald-600 font-bold">
                              {formatCurrency(monthReport.data.reduce((s, r) => s + r.revenue, 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Year-wise Report */}
          <TabsContent value="year" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Year-on-Year Performance</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(yearReport?.data, "year_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !yearReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data available</p>
                ) : (
                  <>
                    <div className="h-72 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={yearReport.data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} />
                          <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} />
                          <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                            formatter={(value, name) => [name === "revenue" ? formatCurrency(value) : value, name]}
                          />
                          <Legend />
                          <Bar yAxisId="left" dataKey="inquiries" name="Inquiries" fill="#60a5fa" />
                          <Bar yAxisId="left" dataKey="quotes" name="Quotes" fill="#2E7D32" />
                          <Bar yAxisId="left" dataKey="bookings" name="Bookings" fill="#C9A227" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Year</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Inquiries</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quote Value</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue Growth</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {yearReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800 font-semibold">{row.year}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.inquiries}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(row.quote_value)}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.bookings}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                              <td className="px-4 py-3 text-right">
                                <GrowthIndicator value={row.revenue_growth} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Destination-wise Report */}
          <TabsContent value="destination" className="space-y-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-gray-800">Destination Performance - {fyLabel(selectedYear)}</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => exportToCSV(destinationReport?.data, "destination_report")}
                  className="border-gray-200 text-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
                  </div>
                ) : !destinationReport?.data?.length ? (
                  <p className="text-gray-500 text-center py-8">No data available for {selectedYear}</p>
                ) : (
                  <>
                    <div className="h-64 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={destinationReport.data.slice(0, 10)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                          <YAxis dataKey="destination" type="category" stroke="#94a3b8" fontSize={12} width={100} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: "#1e293b", border: "1px solid #334155" }}
                            labelStyle={{ color: "#fff" }}
                          />
                          <Legend />
                          <Bar dataKey="total_quotes" name="Quotes" fill="#2E7D32" />
                          <Bar dataKey="total_bookings" name="Bookings" fill="#C9A227" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Destination</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quotes</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Approved</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Total Pax</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Quote Value</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Bookings</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {destinationReport.data.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50/30">
                              <td className="px-4 py-3 text-gray-800 font-medium">{row.destination}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_quotes}</td>
                              <td className="px-4 py-3 text-right text-green-400">{row.approved_quotes}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_pax}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{formatCurrency(row.quote_value)}</td>
                              <td className="px-4 py-3 text-right text-gray-600">{row.total_bookings}</td>
                              <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{formatCurrency(row.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminReports;
